import { api } from "../config/variables";

/**
 * Check if current environment is local or dev
 */
function isLocalOrDev() {
	const hostname = window.location.hostname;
	return (
		hostname === "localhost" ||
		hostname === "127.0.0.1" ||
		hostname.includes("local") ||
		hostname.includes("dev") ||
		api.currentEnv === "dev"
	);
}

/**
 * Register the image caching service worker
 */
export async function registerServiceWorker() {
	if ("serviceWorker" in navigator) {
		if (isLocalOrDev()) {
			try {
				const registrations = await navigator.serviceWorker.getRegistrations();
				await Promise.all(
					registrations.map((registration) => registration.unregister())
				);
				// eslint-disable-next-line no-console
				console.info(
					"[Service Worker] Unregistered existing service worker(s) on local/dev"
				);
				const cacheNames = await caches.keys();
				await Promise.all(
					cacheNames
						.filter((name) => name.includes("image-cache"))
						.map((name) => caches.delete(name))
				);
				return null;
			} catch (error) {
				console.error("[Service Worker] Error unregistering:", error);
			}
		}

		if (api.currentEnv === "production" || api.currentEnv === "staging") {
			try {
				const registration = await navigator.serviceWorker.register(
					"/imageCacheServiceWorker.js",
					{
						scope: "/",
					}
				);

				if (api.currentEnv !== "production") {
					// eslint-disable-next-line no-console
					console.info(
						"[Service Worker] Registration successful:",
						registration.scope
					);
				}

				registration.addEventListener("updatefound", () => {
					const newWorker = registration.installing;

					newWorker.addEventListener("statechange", () => {
						if (
							newWorker.state === "installed" &&
							navigator.serviceWorker.controller &&
							api.currentEnv !== "production"
						) {
							// eslint-disable-next-line no-console
							console.info("[Service Worker] New service worker installed");
						}
					});
				});

				return registration;
			} catch (error) {
				console.error("[Service Worker] Registration failed:", error);
				return null;
			}
		}
	} else {
		console.warn("[Service Worker] Not supported in this browser");
		return null;
	}
}

/**
 * Unregister the service worker (for testing/debugging)
 */
export async function unregisterServiceWorker() {
	if ("serviceWorker" in navigator) {
		const registration = await navigator.serviceWorker.ready;

		const unregistered = await registration.unregister();
		if (api.currentEnv !== "production") {
			// eslint-disable-next-line no-console
			console.info("[Service Worker] Unregistered:", unregistered);
		}

		return unregistered;
	}
}

/**
 * Clear the image cache
 */
export async function clearImageCache() {
	if ("serviceWorker" in navigator) {
		const registration = await navigator.serviceWorker.ready;

		return new Promise((resolve) => {
			const messageChannel = new MessageChannel();

			messageChannel.port1.onmessage = (event) => {
				if (event.data.success) {
					if (api.currentEnv !== "production") {
						// eslint-disable-next-line no-console
						console.info("[Service Worker] Cache cleared successfully");
					}
					resolve(true);
				} else {
					console.error("[Service Worker] Failed to clear cache");
					resolve(false);
				}
			};

			registration.active.postMessage({ type: "CLEAR_CACHE" }, [
				messageChannel.port2,
			]);
		});
	}
}

/**
 * Check if service worker is supported
 */
export function isServiceWorkerSupported() {
	return "serviceWorker" in navigator;
}
